import { createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';

import { useGetPostAnalyticsQuery } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { Loader } from '../../../shared/Loader';
import { Error as ErrorView } from '../../shared/Error';
import { useCloseOnDelete } from '../useCloseOnDelete';
import { AnalyticsTabs } from './AnalyticsTabs';
import { ItemAnalyticsTopbar } from './ItemAnalyticsTopbar';

type Props = {
  postId: string;
};

export function PostAnalyticsDialog(props: Props): React.ReactElement {
  const { postId } = props;
  const { data, loading, error } = useGetPostAnalyticsQuery({ variables: { postId } });

  const classes = useStyles();
  useCloseOnDelete({ loading, deleted: data?.loggedIn?.postById?.deleted || false });

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView error={error} />;
  }

  const plexPostData = data?.loggedIn?.postById;

  if (plexPostData == null) {
    // We are not loading and we did not get an error.
    // This should be illegal state.
    // TODO: redirect to different URL if this occurs
    throw new Error(`Illegal state: ${error} ${loading} ${data}`);
  }

  return (
    <div className={classes.root}>
      <ItemAnalyticsTopbar
        title={plexPostData.title}
        plexId={plexPostData.plex.id}
        plexName={plexPostData.plex.name}
        plexColor={plexPostData.plex.color}
        plexPublishStatus={plexPostData.publishStatus}
        publishedDate={plexPostData.publishedDate}
      />
      <AnalyticsTabs data={plexPostData?.analytics} />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    contentContainer: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },
    contentData: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: spacings.xxlarge,
      paddingRight: spacings.xxlarge * 2,
      paddingTop: spacings.xlarge,
      paddingBottom: spacings.xlarge,
      overflow: 'auto',
      flex: 1,
      width: '100%',
    },
    postTitle: {
      color: colorPalette.dark._900,
      fontWeight: 'bold',
    },
    postContent: {
      marginTop: spacings.small,
    },
  })
);
